import type {
    Organization,
    CreateOrganizationPayload,
    UpdateOrganizationPayload,
    SurveyTemplate,
    UpdateTemplatePayload,
    Survey,
    CreateSurveyPayload,
    UpdateSurveyPayload,
    Respondent,
} from '../types';
import { API_BASE, apiFetch, apiDownload } from '../utils/api-client';

export async function getOrganizations(): Promise<Organization[]> {
    return apiFetch<Organization[]>('/admin/organizations/list');
}

export async function getOrganization(orgId: string): Promise<Organization> {
    return apiFetch<Organization>(`/admin/organizations/get?org_id=${orgId}`);
}

export async function createOrganization(payload: CreateOrganizationPayload): Promise<Organization> {
    return apiFetch<Organization>('/admin/organizations/create', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function updateOrganization(payload: UpdateOrganizationPayload): Promise<Organization> {
    return apiFetch<Organization>('/admin/organizations/update', {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
}

export async function deleteOrganization(orgId: string): Promise<void> {
    await apiFetch<void>(`/admin/organizations/delete?org_id=${orgId}`, {
        method: 'DELETE',
    });
}

// ============================================
// SURVEY TEMPLATE APIs
// ============================================

export async function getTemplates(): Promise<SurveyTemplate[]> {
    return apiFetch<SurveyTemplate[]>('/admin/survey-templates/list');
}

export async function updateTemplateName(payload: UpdateTemplatePayload): Promise<SurveyTemplate> {
    return apiFetch<SurveyTemplate>('/admin/survey-templates/update-name', {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
}

export async function getTemplate(templateId: number): Promise<SurveyTemplate> {
    return apiFetch<SurveyTemplate>(`/admin/survey-templates/get?template_id=${templateId}`);
}

export async function updateQuestion(payload: { question_id: number; text_he: string }): Promise<void> {
    await apiFetch<void>('/admin/questions', {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
}

export function getTemplateImportUrl(): string {
    return `${API_BASE}/admin/survey-templates/import`;
}

export async function getSurveys(): Promise<Survey[]> {
    return apiFetch<Survey[]>('/admin/survey-instances/list');
}

export async function getSurvey(instanceId: string): Promise<Survey> {
    return apiFetch<Survey>(`/admin/survey-instances/get?instance_id=${instanceId}`);
}

export async function createSurvey(payload: CreateSurveyPayload): Promise<Survey> {
    return apiFetch<Survey>('/admin/survey-instances/create', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function updateSurvey(payload: UpdateSurveyPayload): Promise<Survey> {
    return apiFetch<Survey>('/admin/survey-instances/update', {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
}

export async function deleteSurvey(instanceId: string): Promise<void> {
    await apiFetch<void>(`/admin/survey-instances/delete?instance_id=${instanceId}`, {
        method: 'DELETE',
    });
}

export async function downloadInstanceReports(instanceId: string): Promise<void> {
    await apiDownload('/admin/survey-instances/send-instance-reports', {
        method: 'POST',
        body: JSON.stringify({ instance_id: instanceId }),
    });
}

/**
 * Download individual respondent PDF report via n8n
 */
export async function downloadRespondentReport(respondentId: string): Promise<void> {
    await apiDownload('/admin/respondents/download-report', {
        method: 'POST',
        body: JSON.stringify({ respondent_id: respondentId }),
    });
}

/**
 * Download group (survey instance) PDF report via n8n
 */
export async function downloadGroupReport(instanceId: string): Promise<void> {
    await apiDownload('/admin/survey-instances/download-group-report', {
        method: 'POST',
        body: JSON.stringify({ instance_id: instanceId }),
    });
}

/**
 * Download organization PDF report via n8n
 */
export async function downloadOrganizationReport(organizationId: string): Promise<void> {
    await apiDownload('/admin/organizations/download-report', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId }),
    });
}

/**
 * Generates the public registration link for a survey instance
 */
export function getSurveyRegisterUrl(instanceId: string): string {
    // New Netlify domain for the survey client
    return `https://survey.karenavnam.com/register/${instanceId}`;
}

export async function getRespondersByInstance(instanceId: string): Promise<Respondent[]> {
    const data = await apiFetch<any>(`/admin/respondents/survey-instance?instance_id=${instanceId}`);
    if (!data) return [];

    // Handle Array responses
    if (Array.isArray(data)) {
        if (data.length === 1 && data[0] && Array.isArray(data[0].respondents)) return data[0].respondents;
        return data;
    }

    // Handle Object responses (n8n sometimes returns a single object)
    if (typeof data === 'object') {
        if (Array.isArray(data.respondents)) return data.respondents;
        if (data.id) return [data]; // Single respondent object
    }

    return [];
}

export async function getRespondersByTemplate(templateId: number): Promise<Respondent[]> {
    const data = await apiFetch<any>(`/admin/respondents/survey-template?template_id=${templateId}`);
    if (!data) return [];

    if (Array.isArray(data)) {
        if (data.length === 1 && data[0] && Array.isArray(data[0].respondents)) return data[0].respondents;
        return data;
    }

    if (typeof data === 'object') {
        if (Array.isArray(data.respondents)) return data.respondents;
        if (data.id) return [data];
    }

    return [];
}

export async function getRespondersByOrganization(orgId: string): Promise<Respondent[]> {
    const data = await apiFetch<any>(`/admin/respondents/organization?organization_id=${orgId}`);
    if (!data) return [];

    if (Array.isArray(data)) {
        if (data.length === 1 && data[0] && Array.isArray(data[0].respondents)) return data[0].respondents;
        return data;
    }

    if (typeof data === 'object') {
        if (Array.isArray(data.respondents)) return data.respondents;
        if (data.id) return [data];
    }

    return [];
}
